import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
  ArrowSquareOut as ArrowSquareOutIcon,
  Flag as FlagIcon,
  FlagPennant as FlagPennantIcon,
  Sparkle as SparkleIcon,
  Circle as CircleIcon,
  Waveform as WaveformIcon,
  Function as FunctionIcon,
  Warehouse as WarehouseIcon,
  CheckCircle as CheckCircleIcon,
} from '@phosphor-icons/react';
import type { AllocationRow, TraceStep as TraceStepType } from '../data';
import {
  SNAPSHOTS,
  HORIZON,
  findCustomer,
  findProduct,
  findWh,
  findUser,
  whColor,
  buildTrace,
} from '../data';

function TraceStep({ step, isLast }: { step: TraceStepType; isLast: boolean }) {
  const KIND: Record<
    string,
    { color: string; Icon: React.ComponentType<any>; label: string }
  > = {
    input: { color: 'text-stone-500', Icon: CircleIcon, label: 'Input' },
    signal: { color: 'text-amber-700', Icon: WaveformIcon, label: 'Signal' },
    compute: { color: 'text-indigo-700', Icon: FunctionIcon, label: 'Compute' },
    provenance: {
      color: 'text-stone-700',
      Icon: WarehouseIcon,
      label: 'Provenance',
    },
    output: { color: 'text-stone-900', Icon: CheckCircleIcon, label: 'Output' },
  };
  const k = KIND[step.kind];
  return (
    <div className="flex gap-2.5 relative">
      <div className="relative flex flex-col items-center flex-shrink-0">
        <span
          className={cn(
            'inline-flex h-5 w-5 items-center justify-center rounded-full border bg-white text-[11px]',
            k.color,
          )}
          style={{ borderColor: 'currentColor' }}
        >
          <k.Icon size={11} />
        </span>
        {!isLast && <span className="mt-0.5 flex-1 w-px bg-stone-200" />}
      </div>
      <div className="pb-2.5 min-w-0 flex-1">
        <div
          className={cn(
            'text-[10px] font-semibold uppercase tracking-wider',
            k.color,
          )}
        >
          {k.label}
        </div>
        <div className="mt-px text-xs leading-relaxed text-stone-800">
          {step.text}
        </div>
        {step.confidence != null && (
          <div className="mt-1.5 flex items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Confidence
            </span>
            <span className="relative h-1 w-20 rounded-full bg-stone-100">
              <span
                className="absolute left-0 top-0 h-1 rounded-full"
                style={{
                  width: `${step.confidence * 100}%`,
                  background:
                    step.confidence > 0.8
                      ? '#15803d'
                      : step.confidence > 0.6
                        ? '#d97706'
                        : '#b91c1c',
                }}
              />
            </span>
            <span className="text-[11px] font-semibold tabular-nums text-stone-800">
              {Math.round(step.confidence * 100)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function ProvenanceBreakdown({
  row,
  prod,
}: {
  row: AllocationRow;
  prod: { uom: string };
}) {
  const total = row.prov.reduce((s, p) => s + p.qty, 0);
  return (
    <div>
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Provenance — where it ships from
      </div>
      <div className="flex h-2 overflow-hidden rounded-full bg-stone-100">
        {row.prov.map((p, i) => (
          <div
            key={i}
            className="h-full"
            style={{ flex: p.qty, background: whColor(p.wh) }}
          />
        ))}
      </div>
      <div className="mt-2 flex flex-col gap-1">
        {row.prov.map((p, i) => {
          const wh = findWh(p.wh);
          const pct = (p.qty / total) * 100;
          return (
            <div
              key={i}
              className="grid grid-cols-[20px_1fr_auto_auto] items-center gap-2 rounded-md bg-stone-50 p-1.5 px-2"
            >
              <span
                className="inline-flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold text-white"
                style={{ background: whColor(p.wh) }}
              >
                {wh.short[0]}
              </span>
              <div>
                <div className="text-xs font-medium text-stone-900">
                  {wh.name}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {wh.id} · {wh.loc}
                </div>
              </div>
              <div className="text-xs font-semibold tabular-nums text-stone-800">
                {p.qty} {prod.uom}
              </div>
              <div className="min-w-[32px] text-right text-[11px] tabular-nums text-muted-foreground">
                {pct.toFixed(0)}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ConsumptionMeter({
  row,
  prod,
}: {
  row: AllocationRow;
  prod: { uom: string };
}) {
  const cur = row.qty['S-MAY13'];
  const isOver = row.remainder < 0;
  const pct = Math.min(100, (row.consumed / Math.max(cur, 1)) * 100);
  return (
    <div>
      <div className="mb-1 flex justify-between text-[11px] tabular-nums text-stone-800">
        <span>
          Consumed <strong className="text-stone-900">{row.consumed}</strong> /{' '}
          {cur} {prod.uom}
        </span>
        <span
          className={cn(
            'font-semibold',
            isOver ? 'text-red-700' : 'text-muted-foreground',
          )}
        >
          {isOver
            ? `Over by ${Math.abs(row.remainder)}`
            : `${cur - row.consumed} left`}
        </span>
      </div>
      <div className="relative h-2.5 overflow-hidden rounded-full bg-stone-100">
        <div
          className="absolute left-0 top-0 h-full"
          style={{
            width: `${pct}%`,
            background: isOver ? '#b91c1c' : pct > 80 ? '#d97706' : '#525252',
          }}
        />
        {isOver && (
          <div
            className="absolute top-[-1px] h-3 border-t border-b border-red-500"
            style={{
              left: '100%',
              width: `${Math.min(40, Math.abs(row.remainder) * 1.5)}%`,
              background:
                'repeating-linear-gradient(45deg, #fecaca, #fecaca 4px, #ef4444 4px, #ef4444 8px)',
            }}
          />
        )}
      </div>
      <div className="mt-1.5 flex gap-4 text-[11px] text-muted-foreground">
        <span>
          Confirmed: <strong className="text-stone-900">{row.confirmed}</strong>
        </span>
        <span>
          Allocated: <strong className="text-stone-900">{cur}</strong>
        </span>
      </div>
    </div>
  );
}

function SnapshotTimeline({
  row,
  prod,
  fromSnap,
  toSnap,
}: {
  row: AllocationRow;
  prod: { uom: string };
  fromSnap: string;
  toSnap: string;
}) {
  const series = SNAPSHOTS.map((s) => ({ snap: s, qty: row.qty[s.id] }));
  const max = Math.max(...series.map((s) => s.qty));
  const min = Math.min(...series.map((s) => s.qty));
  const range = Math.max(max - min, 1);

  return (
    <div>
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Allocation across snapshots
      </div>
      <div
        className="grid items-end gap-0.5 rounded-md bg-stone-50 px-1 py-1.5"
        style={{
          gridTemplateColumns: `repeat(${series.length}, 1fr)`,
          height: 70,
        }}
      >
        {series.map((s, i) => {
          const isWeekly = s.snap.kind === 'weekly';
          const isFrom = s.snap.id === fromSnap;
          const isTo = s.snap.id === toSnap;
          const h = 14 + ((s.qty - min) / range) * 40;
          return (
            <div key={i} className="flex flex-col items-center gap-0.5 min-w-0">
              <span className="text-[10px] font-semibold tabular-nums text-stone-800">
                {s.qty}
              </span>
              <div
                className="relative w-[70%] rounded-sm"
                style={{
                  height: h,
                  background: isTo
                    ? '#1c1917'
                    : isFrom
                      ? '#78716c'
                      : isWeekly
                        ? '#a5b4fc'
                        : '#d6d3d1',
                }}
              >
                {isWeekly && (
                  <span className="absolute -top-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-indigo-600" />
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div
        className="grid gap-0.5 mt-1"
        style={{ gridTemplateColumns: `repeat(${series.length}, 1fr)` }}
      >
        {series.map((s, i) => (
          <div
            key={i}
            className="text-center text-[9px] leading-tight text-muted-foreground"
          >
            {s.snap.shortLabel.replace(' W', '')}
            {s.snap.kind === 'weekly' && (
              <span className="block text-[8px] font-semibold text-indigo-600">
                anchor
              </span>
            )}
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex gap-3 text-[10px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-sm bg-stone-500" /> Compare from
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-sm bg-stone-900" /> Compare to
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-sm bg-indigo-300" /> Weekly anchor
        </span>
      </div>
    </div>
  );
}

function MiniHorizon({ row }: { row: AllocationRow }) {
  const slice = row.weekly.slice(0, 26);
  const max = Math.max(...slice.map((w) => w.alloc));
  const min = Math.min(...slice.map((w) => w.alloc));
  const range = Math.max(max - min, 1);
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Next 26 weeks
        </span>
        <span className="text-[10px] text-muted-foreground">
          · {row.weekly.filter((w) => w.over).length} at-risk weeks across 52w
        </span>
      </div>
      <div
        className="grid items-end gap-px rounded-md bg-stone-50 py-1"
        style={{
          gridTemplateColumns: `repeat(${slice.length}, 1fr)`,
          height: 44,
        }}
      >
        {slice.map((w, i) => {
          const h = 8 + ((w.alloc - min) / range) * 32;
          const bg = w.over
            ? '#dc2626'
            : w.isCurrent
              ? '#4338ca'
              : w.isPast
                ? '#78716c'
                : '#d6d3d1';
          return (
            <div
              key={i}
              title={`${w.label} · ${w.alloc}${w.consumed != null ? ` · consumed ${w.consumed}` : ' · projected'}${w.over ? ' · OVER' : ''}`}
              style={{
                height: h,
                background: bg,
                borderRadius: 1,
                minWidth: 4,
              }}
            />
          );
        })}
      </div>
      <div className="mt-0.5 flex justify-between">
        <span className="font-mono text-[9px] text-muted-foreground">
          {slice[0].label}
        </span>
        <span className="font-mono text-[9px] font-bold text-indigo-700">
          NOW
        </span>
        <span className="font-mono text-[9px] text-muted-foreground">
          {slice[slice.length - 1].label}
        </span>
      </div>
    </div>
  );
}

export function ExpandedRow({
  row,
  fromSnap,
  toSnap,
  onFlag,
  flagged,
}: {
  row: AllocationRow;
  fromSnap: string;
  toSnap: string;
  onFlag: (rid: string) => void;
  flagged: boolean;
}) {
  const cust = findCustomer(row.customer);
  const prod = findProduct(row.product);
  const sp = findUser(cust.salesperson);
  const trace = useMemo(() => buildTrace(row), [row.rid]);

  return (
    <td colSpan={9} className="border-b border-border bg-stone-50 p-0">
      <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-4 p-4 pb-5">
        {/* LEFT — AI reasoning trace */}
        <div>
          <div className="mb-2.5 flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded bg-gradient-to-r from-violet-100 to-sky-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700">
              <SparkleIcon size={10} /> AI reasoning
            </span>
            <span className="text-[11px] text-muted-foreground">
              Trace for {row.customer} · {row.product} · run on snapshot S-MAY13
            </span>
          </div>
          <div>
            {trace.map((step, i) => (
              <TraceStep key={i} step={step} isLast={i === trace.length - 1} />
            ))}
          </div>
        </div>

        {/* MIDDLE — provenance + consumption */}
        <div className="flex flex-col gap-3.5">
          <ProvenanceBreakdown row={row} prod={prod} />
          <div>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Consumption this week
            </div>
            <ConsumptionMeter row={row} prod={prod} />
          </div>
        </div>

        {/* RIGHT — timeline + account + actions */}
        <div className="flex flex-col gap-3.5">
          <SnapshotTimeline
            row={row}
            prod={prod}
            fromSnap={fromSnap}
            toSnap={toSnap}
          />
          <MiniHorizon row={row} />

          <div>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Account
            </div>
            <div className="text-xs leading-relaxed text-stone-800">
              <div>
                <strong>{cust.name}</strong> · {cust.id}
              </div>
              <div className="text-muted-foreground">
                {cust.loc} · {cust.regionLabel} · {cust.tier} tier
              </div>
              <div className="text-muted-foreground">
                Salesperson:{' '}
                <span className="font-medium text-stone-900">
                  {sp.fullName}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-auto flex gap-1.5">
            {flagged ? (
              <button
                onClick={() => onFlag(row.rid)}
                className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-700 shadow-xs"
              >
                <FlagPennantIcon size={12} weight="fill" /> Flagged · click to
                clear
              </button>
            ) : (
              <button
                onClick={() => onFlag(row.rid)}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-2.5 py-1.5 text-xs font-medium text-stone-900 shadow-xs"
              >
                <FlagIcon size={12} /> Flag for ops review
              </button>
            )}
            <button className="inline-flex items-center gap-1.5 rounded-md border border-transparent bg-transparent px-2.5 py-1.5 text-xs font-medium text-muted-foreground">
              <ArrowSquareOutIcon size={12} /> Open customer
            </button>
          </div>
        </div>
      </div>
    </td>
  );
}
